<template>
  <app-versioning-drawer
    :model-value="checklistVersioningPane.isOpen"
    :documents="checklistVersioningPane.documents"
    :can-manage-versions="canManageVersions"
    v-model:tab="tab"
    :add-disabled="addDisabled"
    :add-loading="addLoading"
    data-locator="checklist-versioning-pane"
    @add="submitAddVersion"
    @close="closePane"
  >
    <template #manage>
      <!-- Custom overlay to manage overlay with pane and other dialogs -->
      <v-overlay v-if="isDocumentOnGoingEditionDialogOpen"></v-overlay>

      <DocumentVersioning ref="versioning" :loading="attachedFileVersionsPending || postAttachedFileVersionsPending"
                          :selected-documents="checklistVersioningPane.documents"
                          @setIsDocumentOnGoingEditionDialogOpen="isDocumentOnGoingEditionDialogOpen = $event"
                          @add-version="onAddVersion"
                          @add-version-from-imanage="onAddVersionFromImanage"
                          @update:add-disabled="addDisabled = $event"
                          @update:add-loading="addLoading = $event"
      />
    </template>
    <template #history>
      <DocumentVersioningHistory :selected-documents="checklistVersioningPane.documents"
                                  @share-version="onShareVersion"
      />
    </template>
  </app-versioning-drawer>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentVersioning from '@/common/document-versioning/DocumentVersioning.vue'
import DocumentVersioningHistory from '@/common/document-versioning/DocumentVersioningHistory.vue'
import { track } from '@/common/pendo/agent'
import { toast } from '@/common/utils/toast'
import {
  CHANGE_CHECKLIST_VERSIONING_PANE_TAB,
  CLOSE_CHECKLIST_VERSIONING_PANE,
  GET_GLOBAL_TASK_FOLDER,
  GET_TASK_FOLDER,
  POST_ATTACHED_FILE_VERSION,
  SET_CHECKLIST_VERSIONING_PANE,
} from '@/store/modules/checklist/action_types'
import { POST_VERSION_FROM_IMANAGE, SET_TABLE_DOCUMENTS_PANE } from '@/store/modules/documents/action_types'
const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistVersioningPane'

export default {
  name: 'ChecklistVersioningPane',
  components: {
    DocumentVersioning,
    DocumentVersioningHistory,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      // Mirrors the state of the add-version form, which now drives a footer rendered outside
      // of it. Disabled until the form says otherwise.
      addDisabled: true,
      addLoading: false,
      isDocumentOnGoingEditionDialogOpen: false,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm', 'currentUserRights']),
    ...mapState('documents', ['numberingEnabled']),
    ...mapState('checklist', ['attachedFileVersionsPending', 'checklistVersioningPane', 'globalAttachedFilesDialogIsOpen', 'postAttachedFileVersionsPending']),
    canManageVersions () {
      return this.isCurrentUserPm || this.currentUserRights.canUploadVersions
    },
    tab: {
      get () {
        return this.checklistVersioningPane.tab
      },
      set (val) {
        this.CHANGE_CHECKLIST_VERSIONING_PANE_TAB(val)
      },
    },
  },
  methods: {
    ...mapActions('checklist', [
      CHANGE_CHECKLIST_VERSIONING_PANE_TAB,
      CLOSE_CHECKLIST_VERSIONING_PANE,
      GET_GLOBAL_TASK_FOLDER,
      GET_TASK_FOLDER,
      POST_ATTACHED_FILE_VERSION,
      SET_CHECKLIST_VERSIONING_PANE,
    ]),
    ...mapActions('documents', [POST_VERSION_FROM_IMANAGE, SET_TABLE_DOCUMENTS_PANE]),
    async onAddVersion (data) {
      try {
        await this.POST_ATTACHED_FILE_VERSION({
          mnemo: this.mnemo,
          documentId: this.checklistVersioningPane.documents[0].id,
          data: data,
        })

        track('new_version_created', { checklistMnemo: this.mnemo, from : 'Checklist' })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionSuccess`))

        if (this.globalAttachedFilesDialogIsOpen) {
          this.GET_GLOBAL_TASK_FOLDER(this.mnemo)
        } else {
          this.GET_TASK_FOLDER({
            mnemo: this.mnemo,
            folderId: this.checklistVersioningPane.documents[0].parent.id,
          })
        }

        this.closePane()
      } catch (e) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionError`))
      }
    },
    async onAddVersionFromImanage (data) {
      try {
        await this.POST_VERSION_FROM_IMANAGE({
          mnemo: this.mnemo,
          data: data,
        })

        track('new_version_created', { checklistMnemo: this.mnemo, from: 'Checklist' })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionFromImanageSuccess`))

        if (this.globalAttachedFilesDialogIsOpen) {
          this.GET_GLOBAL_TASK_FOLDER(this.mnemo)
        } else {
          this.GET_TASK_FOLDER({
            mnemo: this.mnemo,
            folderId: this.checklistVersioningPane.documents[0].parent.id,
          })
        }

        this.closePane()
      } catch (error) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postVersionFromImanageError`))
      }
    },
    // The add action lives in the drawer footer, outside of the scrollable body, so it calls
    // back into the form that owns the version being built.
    submitAddVersion () {
      this.$refs.versioning?.onAddVersion()
    },
    closePane () {
      this.CLOSE_CHECKLIST_VERSIONING_PANE()
    },
    onShareVersion (data) {
      const currentDocument = { ...this.checklistVersioningPane.documents[0] }

      this.CLOSE_CHECKLIST_VERSIONING_PANE()
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        tab: 'share',
        documents: [data],
        checklistFilesParent: currentDocument?.parent,
        forcePmSelection: true,
        previousDocument: this.checklistVersioningPane.documents[0],
        onCloseCallback: () => {
          this.SET_CHECKLIST_VERSIONING_PANE({
            isOpen: true,
            documents: [currentDocument],
            tab: 'history',
          })
        },
      })
    },
  },
}
</script>

